import { useEffect, useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Receipt, Plus, Trash2, Download, Image as ImageIcon, Loader2 } from "lucide-react";
import { toast } from "sonner";

const CATEGORIES = ["carburante", "pedaggio", "pranzo", "hotel", "parcheggio", "materiale", "altro"];
const CAT_LABEL = { carburante: "Carburante", pedaggio: "Pedaggio", pranzo: "Pranzo", hotel: "Hotel", parcheggio: "Parcheggio", materiale: "Materiale", altro: "Altro" };
const fmt = (n) => new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR" }).format(Number(n || 0));

const empty = {
  date: new Date().toISOString().slice(0, 10),
  amount: "",
  category: "altro",
  description: "",
  file: null,
};

export default function Expenses() {
  const { user } = useAuth();
  const [list, setList] = useState([]);
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);
  const [exporting, setExporting] = useState(false);

  const load = () => api.get("/expenses", { params: { month } }).then((r) => setList(r.data));
  useEffect(() => { load(); }, [month]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.file) return toast.error("Allega la foto dello scontrino");
    if (!form.amount) return toast.error("Inserisci l'importo");
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append("file", form.file);
      await api.post("/expenses", fd, {
        params: {
          date: form.date,
          amount: Number(form.amount),
          category: form.category,
          description: form.description,
        },
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Nota spese inviata");
      setForm(empty);
      setOpen(false);
      load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!confirm("Eliminare questa spesa?")) return;
    try { await api.delete(`/expenses/${id}`); toast.success("Eliminata"); load(); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  const exportPdf = async () => {
    setExporting(true);
    try {
      const res = await api.get("/expenses/export", { params: { month }, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = `note_spese_${month}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("PDF generato");
    } catch (_e) { toast.error("Export fallito (solo admin)"); }
    finally { setExporting(false); }
  };

  const totalMonth = list.reduce((s, e) => s + Number(e.amount || 0), 0);

  return (
    <div className="space-y-8">
      <header className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-widest text-sky-600 font-semibold mb-2">Rimborsi</div>
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold text-slate-900 tracking-tight">Nota Spese</h1>
          <p className="text-slate-500 mt-2 text-sm">
            {user?.role === "admin" ? "Rivedi e approva le note spese dei dipendenti." : "Carica le foto degli scontrini delle spese effettuate."}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="w-40 bg-white" data-testid="expenses-month-input" />
          {user?.role === "admin" && (
            <Button onClick={exportPdf} disabled={exporting} variant="outline" className="h-11" data-testid="expenses-export-pdf-button">
              <Download className="w-4 h-4 mr-2" /> {exporting ? "…" : "PDF Mese"}
            </Button>
          )}
          <Button onClick={() => { setForm(empty); setOpen(true); }}
            className="bg-sky-600 hover:bg-sky-700 text-white h-11" data-testid="add-expense-button">
            <Plus className="w-4 h-4 mr-2" /> Nuova
          </Button>
        </div>
      </header>

      {user?.role === "admin" && (
        <div className="bg-slate-900 rounded-2xl p-5 text-white flex items-center justify-between">
          <div>
            <div className="text-xs uppercase tracking-widest font-semibold text-sky-300">Totale {month}</div>
            <div className="font-display text-3xl font-extrabold mt-1" data-testid="expenses-total">{fmt(totalMonth)}</div>
          </div>
          <div className="text-sm text-slate-400">
            {list.length} ricevute
          </div>
        </div>
      )}

      <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden" data-testid="expenses-list">
        {list.length === 0 ? (
          <div className="p-12 text-center text-slate-400">Nessuna nota spese per il mese selezionato.</div>
        ) : (
          <ul className="divide-y divide-slate-100">
            {list.map((e) => (
              <li key={e.id} className="p-4 flex items-center gap-4" data-testid={`expense-${e.id}`}>
                <div className="w-11 h-11 rounded-xl bg-sky-50 border border-sky-100 text-sky-700 flex items-center justify-center shrink-0">
                  <Receipt className="w-5 h-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-slate-900 truncate">{CAT_LABEL[e.category] || e.category}</span>
                    <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full border ${
                      e.status === "approved" ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                      : e.status === "rejected" ? "bg-rose-50 text-rose-700 border-rose-200"
                      : "bg-amber-50 text-amber-700 border-amber-200"
                    }`}>
                      {e.status === "approved" ? "Approvata" : e.status === "rejected" ? "Rifiutata" : "In attesa"}
                    </span>
                  </div>
                  <div className="font-mono text-xs text-slate-500 mt-0.5">
                    {e.date} · {e.user_name}{e.description && ` · ${e.description}`}
                  </div>
                </div>
                <div className="font-mono font-bold text-slate-900 text-right">{fmt(e.amount)}</div>
                <button
                  type="button"
                  onClick={async () => {
                    try {
                      const r = await api.get(`/expenses/${e.id}/receipt`, { responseType: "blob" });
                      const url = URL.createObjectURL(r.data);
                      window.open(url, "_blank");
                      setTimeout(() => URL.revokeObjectURL(url), 60000);
                    } catch (err) {
                      toast.error(formatApiError(err));
                    }
                  }}
                  className="p-2 rounded-lg hover:bg-slate-100 text-slate-500"
                  data-testid={`view-expense-${e.id}`}
                >
                  <ImageIcon className="w-4 h-4" />
                </button>
                {user?.role === "admin" && e.status === "pending" && (
                  <>
                    <button onClick={async () => { await api.patch(`/expenses/${e.id}/status`, { status: "approved" }); toast.success("Approvata"); load(); }}
                      className="text-xs bg-emerald-600 hover:bg-emerald-700 text-white px-2 py-1.5 rounded-lg font-semibold"
                      data-testid={`approve-expense-${e.id}`}>Approva</button>
                    <button onClick={async () => { await api.patch(`/expenses/${e.id}/status`, { status: "rejected" }); toast.success("Rifiutata"); load(); }}
                      className="text-xs bg-slate-200 hover:bg-slate-300 text-slate-700 px-2 py-1.5 rounded-lg font-semibold"
                      data-testid={`reject-expense-${e.id}`}>Rifiuta</button>
                  </>
                )}
                {(user?.role === "admin" || user?.id === e.user_id) && (
                  <button onClick={() => remove(e.id)} className="p-2 rounded-lg hover:bg-rose-50 text-slate-400 hover:text-rose-600"
                    data-testid={`delete-expense-${e.id}`}>
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-display text-2xl font-extrabold">Nuova Nota Spese</DialogTitle></DialogHeader>
          <form onSubmit={submit} className="space-y-4" data-testid="expense-form">
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Data</Label>
                <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} required data-testid="expense-date-input" />
              </div>
              <div><Label>Importo (€)</Label>
                <Input type="number" step="0.01" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} required data-testid="expense-amount-input" />
              </div>
            </div>
            <div>
              <Label>Categoria</Label>
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger data-testid="expense-category-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map(c => <SelectItem key={c} value={c}>{CAT_LABEL[c]}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div><Label>Descrizione</Label>
              <Textarea rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
                placeholder="Es: Pieno furgone AB123CD" data-testid="expense-description-input" />
            </div>
            <div>
              <Label>Foto scontrino / fattura</Label>
              <input ref={fileRef} type="file" accept="image/*,application/pdf" required
                onChange={(e) => setForm({ ...form, file: e.target.files?.[0] || null })}
                className="block w-full mt-1 text-sm" data-testid="expense-file-input" />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => setOpen(false)}>Annulla</Button>
              <Button type="submit" disabled={saving} className="bg-sky-600 hover:bg-sky-700 text-white" data-testid="submit-expense-button">
                {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Invia
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
